import React, { useState, useEffect, useRef, useCallback } from "react";
import type { BaseCity, EnhancedCity } from "@/types/city";

type CitySelectorProps = {
  selectedCity: string;
  onCityChange: (city: { name: string; county: string; countyCode: string }) => void;
  debounceMs?: number;
  limit?: number;
  countryCode?: string;
  language?: string;
};

const CitySelectorForm: React.FC<CitySelectorProps> = ({ 
  selectedCity, 
  onCityChange,
  debounceMs = 500,
  limit = 12,
  countryCode = "RO",
  language = "ro"
}) => {
  const [search, setSearch] = useState(selectedCity || "");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);
  const [cities, setCities] = useState<EnhancedCity[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cache, setCache] = useState<Map<string, EnhancedCity[]>>(new Map());

  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Mapare județe românești pentru coduri
  const countyMapping: Record<string, string> = {
    'alba': 'AB', 'arad': 'AR', 'argeș': 'AG', 'bacău': 'BC', 'bihor': 'BH',
    'bistrița-năsăud': 'BN', 'botoșani': 'BT', 'brăila': 'BR', 'brașov': 'BV',
    'bucurești': 'B', 'buzău': 'BZ', 'caraș-severin': 'CS', 'călărași': 'CL',
    'cluj': 'CJ', 'constanța': 'CT', 'covasna': 'CV', 'dâmbovița': 'DB',
    'dolj': 'DJ', 'galați': 'GL', 'giurgiu': 'GR', 'gorj': 'GJ',
    'harghita': 'HR', 'hunedoara': 'HD', 'ialomița': 'IL', 'iași': 'IS',
    'ilfov': 'IF', 'maramureș': 'MM', 'mehedinți': 'MH', 'mureș': 'MS',
    'neamț': 'NT', 'olt': 'OT', 'prahova': 'PH', 'sălaj': 'SJ',
    'satu mare': 'SM', 'sibiu': 'SB', 'suceava': 'SV', 'teleorman': 'TR',
    'timiș': 'TM', 'tulcea': 'TL', 'vâlcea': 'VL', 'vaslui': 'VS', 'vrancea': 'VN'
  };

  // Orașe populare pentru scoring
  const popular = ['bucuresti', 'cluj-napoca', 'timisoara', 'iasi', 'constanta', 'brasov', 'sibiu', 'oradea', 'ploiesti', 'craiova'];

  // Funcția de transformare pentru datele Nominatim
  const transformNominatimData = useCallback((data: any[]): EnhancedCity[] => {
    return data
      .filter((item: any) => {
        const type = item.type || item.class;
        const category = item.category || '';
        return ['city', 'town', 'village', 'hamlet', 'administrative'].includes(type) || 
               ['place'].includes(item.class) ||
               category === 'place';
      })
      .map((item: any, index: number) => {
        const displayParts = item.display_name.split(',').map((part: string) => part.trim());
        const name = displayParts[0];
        
        let county = '';
        if (item.address) {
          county = item.address.county || 
                  item.address.state || 
                  item.address['ISO3166-2-lvl6'] ||
                  displayParts[displayParts.length - 2] || '';
        } else {
          county = displayParts.length > 2 ? displayParts[displayParts.length - 2] : '';
        }
        
        county = county.replace(/^(județul|county)\s*/i, '').trim();
        
        const countyKey = county.toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');
          
        const countyCode = countyMapping[countyKey] || 
                          county.split(' ')
                            .map(word => word.charAt(0).toUpperCase())
                            .join('')
                            .slice(0, 2) || 
                          'XX';

        const normalized = name.toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[-\s]/g, '');
        
        const countyNorm = county.toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '');

        const city: EnhancedCity = {
          name,
          county,
          countyCode,
          normalized,
          countyNorm,
          isPopular: popular.includes(normalized),
          originalIndex: index,
          score: 0
        };

        return city;
      })
      .filter((city: EnhancedCity) => city.name.trim() !== '' && city.name.length > 1)
      .slice(0, limit);
  }, [countyMapping, popular, limit]);

  // Funcția de căutare prin API
  const fetchCities = useCallback(async (searchQuery: string) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setIsLoading(true);
      setError(null);

      const cacheKey = searchQuery.toLowerCase().trim();
      if (cache.has(cacheKey)) {
        setCities(cache.get(cacheKey)!);
        setIsLoading(false);
        return;
      }

      if (!searchQuery.trim() || searchQuery.trim().length < 2) {
        setCities([]);
        setIsLoading(false);
        return;
      }

      const url = new URL("https://nominatim.openstreetmap.org/search");
      url.searchParams.set('q', `${searchQuery}, Romania`);
      url.searchParams.set('format', 'json');
      url.searchParams.set('countrycodes', countryCode);
      url.searchParams.set('limit', limit.toString());
      url.searchParams.set('addressdetails', '1');
      url.searchParams.set('accept-language', language);
      url.searchParams.set('featuretype', 'city');

      const response = await fetch(url.toString(), {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'CitySelector/1.0'
        },
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const citiesData = transformNominatimData(data);
      
      setCities(citiesData);
      setCache(prev => new Map(prev).set(cacheKey, citiesData));
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Nominatim API Error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Eroare la încărcarea orașelor';
        setError(errorMessage);
        setCities([]);
      }
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  }, [cache, countryCode, language, limit, transformNominatimData]);

  // Debounced search effect
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchCities(search);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [search, fetchCities, debounceMs]);

  // Reset selected index când se schimbă rezultatele
  useEffect(() => {
    setSelectedIndex(-1);
  }, [cities]);

  // Sync cu props
  useEffect(() => {
    if (selectedCity !== search && !showDropdown) {
      setSearch(selectedCity || "");
    }
  }, [search, selectedCity, showDropdown]);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown]);

  // Cleanup la unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSelectCity = useCallback((city: EnhancedCity) => {
    onCityChange({
      name: city.name,
      county: city.county,
      countyCode: city.countyCode
    });
    setSearch(city.name);
    setShowDropdown(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  }, [onCityChange]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (!showDropdown) setShowDropdown(true);
  }, [showDropdown]);

  const scrollToSelected = useCallback((index: number) => {
    if (listRef.current) {
      const item = listRef.current.children[index] as HTMLElement;
      if (item) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = Math.min(selectedIndex + 1, cities.length - 1);
        setSelectedIndex(nextIndex);
        scrollToSelected(nextIndex);
        break;
        
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = Math.max(selectedIndex - 1, -1);
        setSelectedIndex(prevIndex);
        if (prevIndex >= 0) scrollToSelected(prevIndex);
        break;
        
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && cities[selectedIndex]) {
          handleSelectCity(cities[selectedIndex]);
        } else if (cities.length > 0) {
          handleSelectCity(cities[0]);
        }
        break;
        
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
        
      case 'Tab':
        if (selectedIndex >= 0 && cities[selectedIndex]) {
          e.preventDefault();
          handleSelectCity(cities[selectedIndex]);
        }
        break;
    }
  }, [showDropdown, selectedIndex, cities, handleSelectCity, scrollToSelected]);

  // Smart highlight cu performanță optimizată
  const highlightText = useCallback((text: string, query: string) => {
    if (!query.trim()) return text;
    
    const normalizedText = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const normalizedQuery = query.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    
    const index = normalizedText.indexOf(normalizedQuery);
    if (index === -1) return text;
    
    return (
      <>
        {text.slice(0, index)}
        <span className="bg-orange-200 text-orange-900 font-semibold px-0.5 rounded">
          {text.slice(index, index + query.length)}
        </span>
        {text.slice(index + query.length)}
      </>
    );
  }, []);

  const refetch = useCallback(() => {
    fetchCities(search);
  }, [fetchCities, search]);

  const showSuggestions = showDropdown && (cities.length > 0 || search.trim() || error);

  return (
    <div className="relative w-full" ref={ref}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          placeholder="Începe să scrii orașul..."
          value={search}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 text-gray-900 placeholder-gray-500"
          autoComplete="off"
          spellCheck="false"
        />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
          </div>
        )}
      </div>
      
      {showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-80 overflow-hidden">
          {error ? (
            <div className="px-4 py-8 text-center">
              <div className="text-red-400 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm mb-2">Eroare la căutare: {error}</p>
              <button 
                onClick={refetch}
                className="text-orange-600 hover:text-orange-700 text-sm font-medium"
              >
                Încearcă din nou
              </button>
            </div>
          ) : cities.length > 0 ? (
            <>
              <ul ref={listRef} className="overflow-auto max-h-72">
                {cities.map((city: EnhancedCity, index) => (
                  <li
                    key={`${city.name}-${city.countyCode}-${index}`}
                    onClick={() => handleSelectCity(city)}
                    className={`px-4 py-3 cursor-pointer border-b border-gray-50 last:border-b-0 transition-all duration-150 ${
                      index === selectedIndex 
                        ? 'bg-orange-50 border-orange-100' 
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {highlightText(city.name, search)}
                        </div>
                        <div className="text-sm text-gray-500 truncate mt-0.5">
                          {highlightText(city.county, search)}
                        </div>
                      </div>
                      <div className="ml-3 flex-shrink-0">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {city.countyCode}
                        </span>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
              
              <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 text-xs text-gray-500 flex items-center justify-between">
                <span>{cities.length} {cities.length === 1 ? 'oraș găsit' : 'orașe găsite'}</span>
                <span className="text-gray-400">↑↓ navighează • Enter selectează</span>
              </div>
            </>
          ) : isLoading && cities.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <div className="animate-spin w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-500 text-sm">Căutam orașe...</p>
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <div className="text-gray-400 mb-2">
                <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">Nu am găsit orașul "{search}"</p>
              <p className="text-gray-400 text-xs mt-1">Încearcă să scrii diferit sau verifică ortografia</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CitySelectorForm;